import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Disclosure state for an overlay (Modal, Drawer) that is mounted lazily —
 * the pattern a list uses so it does not carry one overlay per row.
 *
 * Mantine's Transition starts in its final state and only animates on
 * updates, so an overlay whose first render already has `opened` set pops
 * into place with no animation, and unmounting it on close cuts the exit
 * animation off. This opens the overlay a frame after mounting it, and keeps
 * it mounted afterwards so closing animates out too.
 *
 * The frame is not decoration. Mantine's Portal renders null until its own
 * layout effect has run, so on the commit that mounts the overlay the
 * Transition inside it does not exist yet; opening on the next state update
 * lands in the same flush as the portal's, and the Transition still gets
 * mounted already open. Waiting for a frame puts the portal's content, and
 * so the closed Transition, on screen first.
 *
 * Render the overlay under `mounted` and pass it `opened`:
 *
 *   const drawer = useAnimatedDisclosure();
 *   ...
 *   {drawer.mounted && <SomeDrawer opened={drawer.opened} onClose={drawer.close} />}
 */
export const useAnimatedDisclosure = () => {
  const [mounted, setMounted] = useState(false);
  const [opened, setOpened] = useState(false);
  const frameRef = useRef(0);

  useEffect(() => () => cancelAnimationFrame(frameRef.current), []);

  const open = useCallback(() => {
    if (mounted) {
      setOpened(true);
      return;
    }
    setMounted(true);
    frameRef.current = requestAnimationFrame(() => setOpened(true));
  }, [mounted]);

  const close = useCallback(() => {
    cancelAnimationFrame(frameRef.current);
    setOpened(false);
  }, []);

  return { mounted, opened, open, close };
};
